import { Box, Text, Button, Flex, Badge } from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import * as routesNames from '../../../constant/routes';

interface TaskNotification {
    taskId: number;
    projectId: number;
    projectName: string;
    taskName: string;

    serviceId: number;
    serviceName: string;

    milestoneId: number;
    milestoneName: string;

    priorityId: number;
    priorityName: string;

    assignedToId: number;
    assignedToName: string;

    reviewerId: number;
    reviewerName: string;

    statusId: number;
    statusName: string;

    date: string;
    startTime: string;
    endTime: string;

    status: 'Upcoming' | 'Running' | 'Overdue';
    message: string;
}

interface Props {
    task: TaskNotification;
    onDismiss: () => void;
}

const TaskReminderPopup = ({
    task,
    onDismiss,
}: Props) => {
    const navigate = useNavigate();

    const getStatusColor = () => {
        switch (task.status) {
            case 'Upcoming':
                return 'orange';

            case 'Running':
                return 'green';

            case 'Overdue':
                return 'red';

            default:
                return 'gray';
        }
    };

    const getPriorityColor = () => {
        switch (task.priorityName) {
            case 'High':
                return 'red';

            case 'Medium':
                return 'orange';

            case 'Low':
                return 'green';

            default:
                return 'gray';
        }
    };

    const handleViewTask = () => {
        navigate(routesNames.MYTASK);
        onDismiss();
    };

    return (
        <Box
            position="fixed"
            bottom="24px"
            right="24px"
            zIndex={9999}
            bg="white"
            borderRadius="12px"
            boxShadow="0 8px 32px rgba(0,0,0,0.18)"
            p={4}
            w="360px"
            borderLeft="4px solid"
            borderLeftColor={
                task.status === 'Overdue'
                    ? 'red.500'
                    : task.status === 'Running'
                        ? 'green.500'
                        : 'orange.400'
            }
            animation="slideIn 0.3s ease"
            sx={{
                '@keyframes slideIn': {
                    from: {
                        transform: 'translateX(100%)',
                        opacity: 0,
                    },
                    to: {
                        transform: 'translateX(0)',
                        opacity: 1,
                    },
                },
            }}
        >
            {/* Header */}
            <Flex
                justify="space-between"
                align="flex-start"
                mb={3}
            >
                <Flex align="center" gap={2}>
                    <CalendarIcon
                        color={
                            task.status === 'Overdue'
                                ? 'red.500'
                                : task.status === 'Running'
                                    ? 'green.600'
                                    : 'orange.500'
                        }
                    />

                    <Text
                        fontWeight="700"
                        fontSize="sm"
                        color="gray.700"
                    >
                        Task Reminder
                    </Text>
                </Flex>

                <Button
                    size="xs"
                    variant="ghost"
                    onClick={onDismiss}
                    color="gray.400"
                    _hover={{
                        color: 'gray.600',
                    }}
                    minW="auto"
                    p={1}
                >
                    ✕
                </Button>
            </Flex>

            {/* Task Name */}
            <Text
                fontWeight="700"
                fontSize="md"
                mb={1}
                noOfLines={2}
                color="gray.800"
            >
                {task.taskName}
            </Text>

            {/* Project */}
            <Text
                fontSize="xs"
                color="gray.500"
                mb={3}
                noOfLines={1}
            >
                {task.projectName}
            </Text>

            {/* Status + Priority */}
            <Flex
                align="center"
                gap={2}
                mb={3}
                flexWrap="wrap"
            >
                <Badge
                    colorScheme={getStatusColor()}
                    borderRadius="full"
                    px={2}
                >
                    {task.status === 'Upcoming' && '⏰ '}
                    {task.status === 'Running' && '▶ '}
                    {task.status === 'Overdue' && '⚠ '}

                    {task.status}
                </Badge>

                <Badge
                    colorScheme={getPriorityColor()}
                    borderRadius="full"
                    px={2}
                >
                    {task.priorityName}
                </Badge>

                <Badge
                    colorScheme="blue"
                    borderRadius="full"
                    px={2}
                >
                    {task.statusName}
                </Badge>
            </Flex>

            {/* Task Information */}
            <Box
                bg="gray.50"
                borderRadius="8px"
                p={3}
                mb={3}
            >
                <Flex
                    justify="space-between"
                    mb={2}
                >
                    <Text
                        fontSize="xs"
                        color="gray.500"
                    >
                        Date
                    </Text>

                    <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.700"
                    >
                        {task.date}
                    </Text>
                </Flex>

                <Flex
                    justify="space-between"
                    mb={2}
                >
                    <Text
                        fontSize="xs"
                        color="gray.500"
                    >
                        Time
                    </Text>

                    <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.700"
                    >
                        {task.startTime} - {task.endTime}
                    </Text>
                </Flex>

                <Flex
                    justify="space-between"
                >
                    <Text
                        fontSize="xs"
                        color="gray.500"
                    >
                        Service
                    </Text>

                    <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.700"
                    >
                        {task.serviceName}
                    </Text>
                </Flex>
            </Box>

            {/* Message */}
            <Text
                fontSize="sm"
                color="gray.600"
                mb={4}
            >
                {task.message}
            </Text>

            {/* Assigned To / Reviewer */}
            <Box mb={4}>
                <Flex
                    justify="space-between"
                    mb={1}
                >
                    <Text
                        fontSize="xs"
                        color="gray.500"
                    >
                        Assigned To
                    </Text>

                    <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.700"
                    >
                        {task.assignedToName}
                    </Text>
                </Flex>

                <Flex
                    justify="space-between"
                >
                    <Text
                        fontSize="xs"
                        color="gray.500"
                    >
                        Reviewer
                    </Text>

                    <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.700"
                    >
                        {task.reviewerName}
                    </Text>
                </Flex>
            </Box>

            {/* Actions */}
            <Flex gap={2}>
                <Button
                    size="sm"
                    colorScheme={
                        task.status === 'Overdue'
                            ? 'red'
                            : 'green'
                    }
                    flex={1}
                    onClick={handleViewTask}
                >
                    View Task
                </Button>

                <Button
                    size="sm"
                    variant="outline"
                    onClick={onDismiss}
                >
                    Dismiss
                </Button>
            </Flex>
        </Box>
    );
};

export default TaskReminderPopup;

